import React, { FC, useMemo } from "react";
import _, { Dictionary } from "lodash";

import { ICustomizationTemplate } from "constants/product-customizations/customization";
import { GoogleFont, GoogleFontLoader, GoogleFontWeight, NUMERIC_WEIGHTS, NumericWeight } from "./google-font-loader";

export type Props<W = "normal", S = "normal"> = { style: S; weight: W };
export type TextAttributeFontProps =
    | Props
    | Props<"bold">
    | Props<NumericWeight>
    | Props<"normal", "italic">
    | Props<"bold", "italic">;
export type TextAttributeFont = { name: string } & TextAttributeFontProps;

export type TextAttributeFontStyle = TextAttributeFont["style"];
export type TextAttributeFontWeight = TextAttributeFont["weight"];

export const textAttributeProps = <
    W extends TextAttributeFontWeight = "normal",
    S extends TextAttributeFontStyle = "normal",
>(
    weight: W,
    style: S,
): Props<W, S> => ({
    weight,
    style,
});

const TEXT_ATTRIBUTE_MODIFIER_RULES: Readonly<Dictionary<TextAttributeFontProps>> = (() => {
    const props = textAttributeProps;
    const bold = props("bold", "normal");
    const italic = props("normal", "italic");
    const bolditalic = props("bold", "italic");

    const rules: Record<string, TextAttributeFontProps> = {
        bold,
        b: bold,
        italic,
        i: italic,
        bolditalic,
        bi: bolditalic,
    };

    NUMERIC_WEIGHTS.forEach((weight) => {
        rules[`${weight}`] = props(weight, "normal");
    });

    return Object.freeze(rules);
})();

export const textAttributeFontWithUrl = (url?: string): TextAttributeFont | undefined => {
    if (!url) {
        return;
    }

    const regex =
        /^https:\/\/fonts\.googleapis\.com\/css\?family=(?<font>[[a-zA-Z0-9\+]{1,})(?::(?<weight>[1-9]00|b|bi|i|bold|italic|bolditalic))?(?:\&.*)?$/;
    const group = regex.exec(url);
    if (!group?.length) {
        return;
    }

    const [name, modifier] = group.slice(1) as [string, string];
    if (!name) {
        return;
    }

    const props = TEXT_ATTRIBUTE_MODIFIER_RULES[modifier] ?? textAttributeProps("normal", "normal");

    return {
        name: name.replace(/\++/g, " ").trim(),
        ...props,
    };
};

export const googleFontWithTextAttributeFont = ({ name, style, weight }: TextAttributeFont): GoogleFont => {
    const font = (weight?: GoogleFontWeight): GoogleFont => ({
        name,
        ...(_.isNil(weight) ? {} : { weights: [weight] }),
    });

    if (style === "italic") {
        if (weight === "bold") {
            return font("bolditalic");
        }

        return font("italic");
    }

    if (weight === "bold" || _.includes(NUMERIC_WEIGHTS, weight)) {
        return font(weight as GoogleFontWeight);
    }

    return font();
};

export type ProductCustomizationTemplateFontLoaderProps = {
    template?: ICustomizationTemplate;
};

export const ProductCustomizationTemplateFontLoader: FC<ProductCustomizationTemplateFontLoaderProps> = ({
    template,
}) => {
    const fonts = useMemo(
        () =>
            template?.attributes
                .flatMap((attribute) => attribute.printed?.text?.font?.values)
                .filter(Boolean)
                .map((value) => textAttributeFontWithUrl(value.string))
                .filter(Boolean)
                .map(googleFontWithTextAttributeFont),
        [template],
    );

    if (!fonts) {
        return null;
    }

    return <GoogleFontLoader fonts={fonts} display={"swap"} />;
};
